import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { API_ENDPOINTS } from '../config/api';
import { companyUserHome } from '../utils/landing';

// ── Brand tokens ────────────────────────────────────────────────────────────
const INK = '#1E2138';
const SLATE = '#5B6478';
const FONT = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";
const MONO = "'JetBrains Mono', Consolas, 'Courier New', monospace";
const SCORE_GRADIENT = 'linear-gradient(90deg, #1B96D0 0%, #68C9AE 60%, #7EC845 100%)';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ── Content ─────────────────────────────────────────────────────────────────
const FEED = [
  { text: 'Intake call with hiring manager is completed', role: 'Senior Backend Engineer', color: '#8ED8FB' },
  { text: 'Candidate scored 312 of 400', role: 'Senior Backend Engineer', color: '#B5E08A' },
  { text: 'Tab switch flagged for review', role: 'Frontend Engineer, round 1', color: '#F7B98E' },
  { text: 'Screening plan ready for recruiter review', role: 'Data Engineer', color: '#B9B3F5' },
  { text: 'Interview report sent to hiring manager', role: 'DevOps Engineer', color: '#68C9AE' },
  { text: 'Live interview started', role: 'Mobile Engineer (iOS)', color: '#F5D66B' },
];
const FEED_TIMES = ['just now', '1 min ago', '3 min ago', '6 min ago'];

const ROLE_CARDS = [
  { label: 'Backend engineer', color: '#8ED8FB', left: '4%', top: '36%', rotate: -38, speed: -70, spin: 6 },
  { label: 'Frontend engineer', color: '#B9B3F5', left: '24%', top: '3%', rotate: -16, speed: 50, spin: -4 },
  { label: 'Data engineer', color: '#F7B98E', left: '52%', top: '0%', rotate: 8, speed: -40, spin: 5 },
  { label: 'DevOps and SRE', color: '#B5E08A', left: '73%', top: '14%', rotate: 32, speed: 60, spin: -6 },
  { label: 'Mobile engineer', color: '#68C9AE', left: '76%', top: '58%', rotate: 46, speed: -55, spin: 4 },
  { label: 'ML engineer', color: '#F5D66B', left: '12%', top: '70%', rotate: -26, speed: 45, spin: -5 },
  { label: 'QA automation', color: '#7FB2E5', left: '48%', top: '70%', rotate: 12, speed: -35, spin: 3 },
];
const BLOB_SHAPES = [
  '42% 58% 63% 37% / 41% 44% 56% 59%',
  '58% 42% 38% 62% / 55% 38% 62% 45%',
  '38% 62% 55% 45% / 62% 47% 53% 38%',
];

const LIVE_STEPS = ['Reading the role brief', 'Interviewing live', 'Scoring against your rubric', 'Report sent to hiring manager'];

const CONVERSATION = [
  { who: 'Alex, AI interviewer', ai: true, text: 'Your limiter works on one server. What changes when the API runs on five?' },
  { who: 'Candidate', ai: false, text: 'The counters need to be shared. I would move them to Redis with an atomic increment and a TTL per window.' },
  { who: 'Alex, AI interviewer', ai: true, text: 'Good. How do you stop two requests racing on the same key right at the window boundary?' },
];
const CODE_COLORS = { kw: '#5ED3C7', num: '#F2C66D', str: '#A8D97A', com: '#6B7F99', txt: '#D6E2F0' };
const CODE_LINES = [
  [['kw', 'import'], ['txt', ' time']],
  [['kw', 'import'], ['txt', ' redis']],
  [],
  [['txt', 'r = redis.Redis()']],
  [],
  [['kw', 'def'], ['txt', ' allow(user_id, limit='], ['num', '100'], ['txt', ', window='], ['num', '60'], ['txt', '):']],
  [['txt', '    key = '], ['str', 'f"rl:{user_id}:{int(time.time() // window)}"']],
  [['txt', '    count = r.incr(key)']],
  [['txt', '    '], ['kw', 'if'], ['txt', ' count == '], ['num', '1'], ['txt', ':']],
  [['txt', '        r.expire(key, window)']],
  [['txt', '    '], ['kw', 'return'], ['txt', ' count <= limit']],
  [],
  [['com', '# next: make incr + expire atomic in a pipeline']],
];
const SCORE_DIMENSIONS = [['Problem solving', 72], ['Code quality', 64], ['System design', 58], ['Communication', 70], ['Testing', 48]];

const HOW = [
  { title: 'Hiring manager intake', text: 'Jessica, the AI intake agent, runs a 15-minute call with the hiring manager and turns it into a structured role brief.' },
  { title: 'Screening plan', text: 'Quali5d drafts the interview blueprint and screening questions. Your recruiter reviews and edits before anything reaches a candidate.' },
  { title: 'Live AI interview', text: 'Alex interviews each candidate live in a real code editor, on their schedule. Your hiring manager gets a scored report and decides.' },
];

const TRUST = [
  {
    title: 'Integrity on every interview',
    text: 'Built for the era of AI interview copilots. Signals are flagged for review, never auto-rejected.',
    points: ['Identity matched to the screening test', 'Tab switches and paste events logged', 'Follow-ups based on the candidate’s own code'],
    color: '#8ED8FB',
  },
  {
    title: 'Your hiring manager decides',
    text: 'Quali5d runs the first round. People make the call, with the full recording and transcript behind every score.',
    points: ['Replay any moment behind a score', 'Structured 0–400 scoring', 'Advance or decline in one click'],
    color: '#B5E08A',
  },
  {
    title: 'Fair to every candidate',
    text: 'Every candidate gets the same structured interview, and knows up front they are talking to an AI.',
    points: ['Clear AI disclosure', 'Recording only with consent', 'Interviews on the candidate’s schedule'],
    color: '#B9B3F5',
  },
];

const PLANS = [
  { name: 'Essential', price: '$99', tag: 'Role briefs and screening questions from any job description.', features: ['AI recruiter documents', 'Screening question sets', 'Recruiter workspace'] },
  { name: 'Professional', price: '$199', tag: 'Adds AI intake calls with your hiring managers.', features: ['Everything in Essential', 'AI hiring manager intake calls', 'Structured feedback capture'] },
  { name: 'Enterprise', price: '$499', tag: 'Adds live AI technical interviews with candidates.', features: ['Everything in Professional', 'Live AI technical interviews', 'Scoring, integrity reports, HM decisions'], highlight: true },
];

const FAQS = [
  ['Do candidates know they are talking to an AI?', 'Yes. Every candidate is told up front that Alex is an AI interviewer, and interviews are recorded only with their consent.'],
  ['Does it replace our engineers’ judgment?', 'No. Quali5d runs the first round. Your hiring manager reviews every report, can replay any moment, and makes the decision.'],
  ['How is this different from a take-home test?', 'A take-home can be finished by anyone, or anything. Alex talks with the candidate live and asks follow-ups about the code they just wrote.'],
  ['Does it work with our ATS?', 'Tell us which ATS you use when you start your pilot and we will confirm the integration for your setup. Current integrations are listed on our Integrations page.'],
];

const COUNTRIES = [
  ['United States', '+1'], ['United Kingdom', '+44'], ['India', '+91'], ['Canada', '+1'], ['Australia', '+61'],
  ['Germany', '+49'], ['France', '+33'], ['Netherlands', '+31'], ['Singapore', '+65'], ['UAE', '+971'],
  ['Saudi Arabia', '+966'], ['South Africa', '+27'], ['Nigeria', '+234'], ['Kenya', '+254'], ['Brazil', '+55'],
  ['Mexico', '+52'], ['Japan', '+81'], ['South Korea', '+82'], ['Philippines', '+63'], ['Malaysia', '+60'],
  ['Ireland', '+353'], ['Sweden', '+46'], ['Norway', '+47'], ['Denmark', '+45'], ['New Zealand', '+64'],
];

const NAV = [['how', 'How it works'], ['integrity', 'Integrity'], ['pricing', 'Pricing'], ['faq', 'FAQ']];

// ── Small building blocks ───────────────────────────────────────────────────
function Blob({ color, size = 44, shape = 0 }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block shrink-0"
      style={{ width: size, height: size, background: color, borderRadius: BLOB_SHAPES[shape % BLOB_SHAPES.length], boxShadow: 'inset -6px -8px 14px rgba(0,0,0,0.06)' }}
    />
  );
}

function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (!('IntersectionObserver' in window) || prefersReducedMotion()) { setShown(true); return undefined; }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setShown(true); io.disconnect(); }
    }, { rootMargin: '0px 0px -8% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={`q5-reveal ${shown ? 'q5-in' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function StepIcon({ state }) {
  if (state === 'done') {
    return (
      <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: '#7EC845' }}>
        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
      </span>
    );
  }
  if (state === 'active') {
    return <span className="w-5 h-5 rounded-full border-2 border-[#1B96D0] border-t-transparent animate-spin shrink-0" />;
  }
  return <span className="w-5 h-5 rounded-full border-2 border-[#E3E7EE] shrink-0" />;
}

function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loginData, setLoginData] = useState({ usernameOrEmail: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showForgotPassword,  setShowForgotPassword]  = useState(false);
  const [forgotEmail,         setForgotEmail]         = useState('');
  const [forgotLoading,       setForgotLoading]       = useState(false);
  const [forgotMessage,       setForgotMessage]       = useState('');
  const [forgotSent,          setForgotSent]          = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(0);
  const [feedTick, setFeedTick] = useState(0);
  const [stepTick, setStepTick] = useState(1);
  const [demoForm, setDemoForm] = useState({ firstName: '', lastName: '', country: '', phone: '', email: '' });
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [showWelcomeToast, setShowWelcomeToast] = useState(false);
  const lenisRef = useRef(null);
  const videoWrapRef = useRef(null);
  const roleSectionRef = useRef(null);
  const roleCardRefs = useRef([]);

  // ── Smooth (inertia) scrolling ──
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let frame = requestAnimationFrame(function raf(time) {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    });
    return () => { cancelAnimationFrame(frame); lenis.destroy(); lenisRef.current = null; };
  }, []);

  // Pause smooth scrolling while a popup is open, so the popup scrolls normally
  useEffect(() => {
    const lenis = lenisRef.current;
    if (!lenis) return;
    if (showLoginModal || showForgotPassword) lenis.stop(); else lenis.start();
  }, [showLoginModal, showForgotPassword]);

  // ── Header blend + scroll-linked motion (video grows in, role cards drift) ──
  useEffect(() => {
    const reduce = prefersReducedMotion();
    let ticking = false;
    const update = () => {
      ticking = false;
      setScrolled(window.scrollY > 8);
      if (reduce) return;
      const vh = window.innerHeight;
      const video = videoWrapRef.current;
      if (video) {
        const r = video.getBoundingClientRect();
        const t = Math.min(1, Math.max(0, (vh - r.top) / (vh * 0.75)));
        video.style.transform = `scale(${0.9 + 0.1 * t})`;
      }
      const sec = roleSectionRef.current;
      if (sec) {
        const r = sec.getBoundingClientRect();
        const p = (r.top + r.height / 2 - vh / 2) / vh;
        roleCardRefs.current.forEach((el, i) => {
          if (!el) return;
          const c = ROLE_CARDS[i];
          el.style.transform = `translate3d(0, ${(p * c.speed).toFixed(1)}px, 0) rotate(${(c.rotate + p * c.spin).toFixed(2)}deg)`;
        });
      }
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); };
  }, []);

  // ── Highlight the menu item for the section in view ──
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const navIds = NAV.map(([id]) => id);
    const els = Array.from(document.querySelectorAll('section'));
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) setActiveSection(navIds.includes(e.target.id) ? e.target.id : ''); });
    }, { rootMargin: '-45% 0px -50% 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // ── "Alex working now" feed and live interview steps ──
  useEffect(() => {
    if (prefersReducedMotion()) { setStepTick(4); return undefined; }
    const feed = setInterval(() => setFeedTick(n => n + 1), 2600);
    const steps = setInterval(() => setStepTick(n => (n + 1) % 6), 1500);
    return () => { clearInterval(feed); clearInterval(steps); };
  }, []);

  // Detect ?welcome=1 after Stripe payment redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('welcome') === '1') {
      setShowWelcomeToast(true);
      // Pre-fill login with saved username from registration
      const savedUsername = localStorage.getItem('quali5d_pending_username');
      if (savedUsername) {
        setLoginData(prev => ({ ...prev, usernameOrEmail: savedUsername }));
        localStorage.removeItem('quali5d_pending_username');
      }
      // Open the sign-in popup so the new customer can log straight in
      setShowLoginModal(true);
      // Clean URL without reload
      window.history.replaceState({}, '', '/');
      // Auto-dismiss after 6 seconds
      const t = setTimeout(() => setShowWelcomeToast(false), 6000);
      return () => clearTimeout(t);
    }
  }, [location.search]);

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    setLoginError('');
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    if (!loginData.usernameOrEmail || !loginData.password) {
      setLoginError('Please enter both username/email and password');
      return;
    }

    setIsLoggingIn(true);
    setLoginError('');

    try {
      // Use JWT login endpoint with Form data
      const formData = new URLSearchParams();
      formData.append('username_or_email', loginData.usernameOrEmail.trim().toLowerCase());
      formData.append('password', loginData.password);

      const response = await fetch(API_ENDPOINTS.AUTH_LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        // Store JWT token and user data
        localStorage.setItem('token', result.access_token);
        localStorage.setItem('user', JSON.stringify(result.user));

        if (result.company) {
          localStorage.setItem('company', JSON.stringify(result.company));
        }

        // Redirect based on user role
        const userRole = result.user.role;

        if (userRole === 'master_admin') {
          navigate('/dashboard/master-admin');
        } else if (userRole === 'company_super_admin') {
          navigate('/dashboard/company-admin');
        } else if (userRole === 'company_user') {
          navigate(await companyUserHome(result.access_token));   // interviewer-only people land on My interviews
        } else if (userRole === 'support' || userRole === 'SUPPORT') {
          navigate('/support');
        }
      } else {
        // Handle different error formats
        let errorMessage = 'Login failed. Please try again.';

        if (result.detail) {
          if (typeof result.detail === 'string') {
            errorMessage = result.detail;
          } else if (Array.isArray(result.detail)) {
            errorMessage = result.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          } else if (typeof result.detail === 'object') {
            errorMessage = result.detail.msg || JSON.stringify(result.detail);
          }
        }

        setLoginError(errorMessage);
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Connection error. Please check if the backend is running.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage('');
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    try {
      const res  = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: forgotEmail.trim().toLowerCase() }),
      });
      const json = await res.json();
      setForgotSent(true);
      setForgotMessage(json.message || 'Reset link sent. Check your inbox.');
    } catch (err) {
      setForgotMessage('Connection error. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleDemoSubmit = async () => {
    setDemoError('');
    const { firstName, lastName, country, phone, email } = demoForm;
    if (!firstName || !lastName || !country || !phone || !email) { setDemoError('Please fill in all fields.'); return; }
    const freeDomains = ['gmail.com','yahoo.com','hotmail.com','outlook.com','icloud.com','aol.com','protonmail.com','live.com','yahoo.co.in','rediffmail.com'];
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain || freeDomains.includes(domain)) { setDemoError('Please use your official company email address.'); return; }
    setDemoLoading(true);
    try {
      const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${BASE}/api/contact/demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: firstName, last_name: lastName, country, phone, email }),
      });
      if (res.ok) { setDemoSuccess(true); }
      else { setDemoError('Something went wrong. Please try again.'); }
    } catch { setDemoError('Connection error. Please try again.'); }
    finally { setDemoLoading(false); }
  };

  const scrollToId = (id) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -84 });
    else el.scrollIntoView({ behavior: 'smooth' });
  };
  const openLogin = () => { setMobileMenuOpen(false); setShowLoginModal(true); };
  const goPricingPage = (e) => { e.preventDefault(); navigate('/pricing'); };

  const inputClass = 'w-full px-4 py-3 bg-white border border-[#E3E7EE] rounded-xl text-sm text-[#1E2138] focus:border-[#1E2138] focus:ring-4 focus:ring-[#1E2138]/5 outline-none transition-all';
  const pillDark = 'inline-flex items-center justify-center rounded-full bg-[#1E2138] px-6 py-3 text-[15px] font-semibold text-white transition-all duration-300 hover:bg-[#30355A] hover:-translate-y-0.5';
  const pillLight = 'inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-[15px] font-semibold text-[#1E2138] ring-1 ring-[#E3E7EE] transition-all duration-300 hover:ring-[#1E2138]/30 hover:-translate-y-0.5';
  const h2Class = 'text-3xl md:text-[40px] font-semibold leading-[1.12] tracking-[-0.03em]';
  const cardShadow = { boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04), 0 12px 32px -12px rgba(16, 24, 40, 0.10)' };

  const feedItems = [0, 1, 2, 3].map(k => ({ key: feedTick - k, ...FEED[((feedTick - k) % FEED.length + FEED.length) % FEED.length] }));

  return (
    <div className="min-h-screen bg-white antialiased" style={{ fontFamily: FONT, color: INK }}>
      <Helmet>
        <title>Quali5d — AI Recruitment Intelligence Platform for Enterprise Hiring</title>
                <meta name="description" content="Live AI technical interviews for engineering hiring teams. AI intake calls, adaptive coding interviews, and scored reports the same day. Flat monthly pricing." />
        <link rel="canonical" href="https://www.quali5d.com/" />
        <meta name="robots" content="index, follow, max-image-preview:large" />

        {/* Open Graph */}
        <meta property="og:title" content="Quali5d — AI Recruitment Intelligence Platform" />
        <meta property="og:description" content="Self-improving AI for enterprise hiring. AI intake calls, AI candidate interviews, integrity-scored reports — in minutes, not days." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.quali5d.com/" />
        <meta property="og:image" content="https://www.quali5d.com/og-image.png" />
        <meta property="og:site_name" content="Quali5d" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Quali5d — AI Recruitment Intelligence" />
        <meta name="twitter:description" content="Self-improving AI for enterprise hiring. AI intake calls, AI candidate interviews, integrity-scored reports — in minutes, not days." />
        <meta name="twitter:image" content="https://www.quali5d.com/og-image.png" />

        {/* Fonts: Inter (everything), JetBrains Mono (code window only) */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=JetBrains+Mono&display=swap" />
      </Helmet>

      <style>{`
        html.lenis, html.lenis body { height: auto; }
        .lenis.lenis-smooth { scroll-behavior: auto !important; }
        .lenis.lenis-smooth [data-lenis-prevent] { overscroll-behavior: contain; }
        .lenis.lenis-stopped { overflow: hidden; }
        .q5-reveal { opacity: 0; transform: translateY(28px); transition: opacity 0.9s cubic-bezier(0.2, 0.7, 0.2, 1), transform 0.9s cubic-bezier(0.2, 0.7, 0.2, 1); }
        .q5-in { opacity: 1; transform: none; }
        @keyframes q5-feed-in { from { opacity: 0; transform: translateY(-14px) scale(0.98); } to { opacity: 1; transform: none; } }
        .q5-feed-new { animation: q5-feed-in 0.6s cubic-bezier(0.2, 0.7, 0.2, 1); }
        @keyframes q5-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
        .q5-pulse { animation: q5-pulse 1.6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .q5-reveal { opacity: 1; transform: none; transition: none; }
          .q5-feed-new, .q5-pulse { animation: none; }
        }
      `}</style>

      {/* ========== HEADER: transparent at top, frosted glass on scroll ========== */}
      <header
        className="sticky top-0 z-40 transition-all duration-500"
        style={{
          background: scrolled || mobileMenuOpen ? 'rgba(255, 255, 255, 0.72)' : 'rgba(255, 255, 255, 0)',
          backdropFilter: scrolled || mobileMenuOpen ? 'saturate(180%) blur(16px)' : 'none',
          WebkitBackdropFilter: scrolled || mobileMenuOpen ? 'saturate(180%) blur(16px)' : 'none',
          boxShadow: scrolled ? '0 1px 0 rgba(16, 24, 40, 0.06)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center justify-between h-[76px]">
            <a href="/" aria-label="Quali5d home" className="flex items-center">
              <img src="/quali5d-logo.png" alt="Quali5d" width="81" height="52" className="h-[52px] w-auto" />
            </a>
            <nav className="hidden md:flex items-center gap-1">
              {NAV.map(([id, label]) => (
                <a
                  key={id}
                  href={id === 'pricing' ? '/pricing' : `#${id}`}
                  onClick={(e) => { e.preventDefault(); scrollToId(id); }}
                  className={`px-4 py-2 rounded-full text-[15px] font-medium transition-all duration-300 ${activeSection === id ? 'bg-[#EEF1F5] text-[#1E2138]' : 'text-[#5B6478] hover:text-[#1E2138]'}`}
                >
                  {label}
                </a>
              ))}
            </nav>
            <div className="hidden md:flex items-center gap-5">
              <button onClick={openLogin} className="text-[15px] font-medium text-[#1E2138] hover:opacity-70 transition-opacity">Sign in</button>
              <button onClick={() => scrollToId('pilot')} className="inline-flex items-center rounded-full bg-[#1E2138] px-5 py-2.5 text-[15px] font-semibold text-white transition-all duration-300 hover:bg-[#30355A]">Start a free pilot</button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-full hover:bg-black/5" aria-label="Open menu">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 12h16M4 17h16" />}
              </svg>
            </button>
          </div>
          {mobileMenuOpen && (
            <nav className="md:hidden flex flex-col gap-1 pb-5">
              {NAV.map(([id, label]) => (
                <a key={id} href={id === 'pricing' ? '/pricing' : `#${id}`} onClick={(e) => { e.preventDefault(); scrollToId(id); }} className="px-4 py-3 rounded-2xl text-[#1E2138] font-medium hover:bg-black/5">{label}</a>
              ))}
              <button onClick={openLogin} className="text-left px-4 py-3 rounded-2xl font-medium hover:bg-black/5">Sign in</button>
              <button onClick={() => scrollToId('pilot')} className={`${pillDark} mt-2`}>Start a free pilot</button>
            </nav>
          )}
        </div>
      </header>

      {/* ========== HERO ========== */}
      <section id="top" className="relative overflow-hidden -mt-[76px] pt-[76px]">
        <div aria-hidden="true" className="absolute -top-40 left-1/2 -translate-x-[70%] w-[640px] h-[640px] rounded-full blur-3xl opacity-40 pointer-events-none" style={{ background: 'radial-gradient(circle, #BFE6FA 0%, rgba(191,230,250,0) 70%)' }} />
        <div aria-hidden="true" className="absolute -top-24 left-1/2 translate-x-[5%] w-[560px] h-[560px] rounded-full blur-3xl opacity-40 pointer-events-none" style={{ background: 'radial-gradient(circle, #D9F0C4 0%, rgba(217,240,196,0) 70%)' }} />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-8 pt-20 md:pt-28 text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-1.5 text-[13px] font-medium text-[#5B6478] ring-1 ring-[#E3E7EE]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7EC845] q5-pulse" /> AI technical interviews for engineering teams
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 text-[38px] md:text-5xl font-semibold leading-[1.08] tracking-[-0.035em]">
              Your first-round technical interview, run by AI.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-5 text-lg md:text-xl text-[#5B6478] leading-relaxed max-w-2xl mx-auto">
              Alex interviews engineering candidates live, adapts to their code, and sends your hiring manager a scored report the same day.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button onClick={() => scrollToId('pilot')} className={pillDark}>Start a free pilot</button>
              <button onClick={() => scrollToId('demo')} className={pillLight}>Watch the demo</button>
            </div>
            <p className="mt-4 text-sm text-[#5B6478]">One open role, up to 10 candidates, no card required.</p>
          </Reveal>
        </div>

        {/* Demo video, grows into place as you scroll */}
        <div id="demo" className="relative max-w-6xl mx-auto px-5 lg:px-8 mt-14 md:mt-20 pb-6">
          <div ref={videoWrapRef} className="rounded-[28px] overflow-hidden bg-[#1E2138] will-change-transform" style={{ transform: 'scale(0.9)', transformOrigin: 'center top', boxShadow: '0 40px 80px -24px rgba(27, 85, 144, 0.28)' }}>
            {/* Video player — file hosted on Azure Blob Storage */}
            <video controls preload="metadata" playsInline className="w-full block aspect-video">
              <source src="https://quali5dstorage.blob.core.windows.net/videos/quali5d%20Video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* ========== CREDIBILITY ========== */}
      <section className="py-14 md:py-16">
        <Reveal className="max-w-3xl mx-auto px-5 text-center">
          <p className="text-lg md:text-xl leading-relaxed text-[#1E2138]">
            Designed and built by talent acquisition leaders with <span className="font-semibold">25+ years of global hiring experience</span> across the US, EMEA and APAC.
          </p>
        </Reveal>
      </section>

      {/* ========== MEET ALEX: live activity feed ========== */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <Reveal>
            <h2 className={h2Class}>Meet Alex, your AI interviewer.</h2>
            <p className="mt-5 text-lg text-[#5B6478] leading-relaxed">Alex runs first-round technical interviews around the clock, so your engineers stay on the roadmap and your recruiters stop chasing calendars.</p>
            <button onClick={() => scrollToId('pilot')} className={`${pillDark} mt-8`}>Start a free pilot</button>
          </Reveal>
          <Reveal delay={120}>
            <div className="rounded-[28px] bg-[#F5F7FA] p-5 md:p-6">
              <p className="flex items-center gap-2 px-1 pb-4 text-sm font-medium text-[#5B6478]">
                <span className="w-2 h-2 rounded-full bg-[#7EC845] q5-pulse" /> Alex working now
              </p>
              <div className="space-y-3">
                {feedItems.map((item, i) => (
                  <div key={item.key} className={`flex items-center gap-4 rounded-2xl bg-white px-4 py-3.5 ${i === 0 ? 'q5-feed-new' : ''}`} style={{ ...cardShadow, opacity: 1 - i * 0.18 }}>
                    <Blob color={item.color} size={36} shape={i} />
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-medium truncate">{item.text}</p>
                      <p className="text-[13px] text-[#5B6478] truncate">{item.role}</p>
                    </div>
                    <span className="text-xs text-[#5B6478] shrink-0">{FEED_TIMES[i]}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ========== EVERY ENGINEERING ROLE: floating cards ========== */}
      <section ref={roleSectionRef} className="relative overflow-hidden py-16 md:py-0">
        <div className="hidden md:block relative max-w-7xl mx-auto h-[800px]">
          {ROLE_CARDS.map((c, i) => (
            <div
              key={c.label}
              ref={el => { roleCardRefs.current[i] = el; }}
              className="absolute w-[200px] h-[200px] rounded-[28px] bg-white border border-[#EEF1F5] p-6 flex flex-col justify-between will-change-transform"
              style={{ left: c.left, top: c.top, transform: `rotate(${c.rotate}deg)`, ...cardShadow }}
            >
              <Blob color={c.color} size={46} shape={i} />
              <p className="text-xl font-medium leading-snug tracking-[-0.01em]">{c.label}</p>
            </div>
          ))}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Reveal className="text-center max-w-xl px-5 pointer-events-auto">
              <h2 className={h2Class}>One interviewer for every engineering role.</h2>
              <p className="mt-5 text-lg text-[#5B6478] leading-relaxed">From backend and data to mobile and DevOps, Alex adapts the interview to the role brief your hiring manager defined.</p>
              <button onClick={() => scrollToId('pilot')} className={`${pillDark} mt-8`}>Book a walkthrough</button>
            </Reveal>
          </div>
        </div>
        {/* Mobile version */}
        <div className="md:hidden px-5 text-center">
          <h2 className={h2Class}>One interviewer for every engineering role.</h2>
          <p className="mt-4 text-lg text-[#5B6478] leading-relaxed">Alex adapts the interview to the role brief your hiring manager defined.</p>
          <div className="mt-8 grid grid-cols-2 gap-3 text-left">
            {ROLE_CARDS.map((c, i) => (
              <div key={c.label} className="rounded-2xl bg-white border border-[#EEF1F5] p-4 flex items-center gap-3" style={cardShadow}>
                <Blob color={c.color} size={28} shape={i} />
                <span className="text-[15px] font-medium">{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WATCH A FIRST ROUND: live steps + product window ========== */}
      <section className="py-16 md:py-24 bg-[#F5F7FA]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <h2 className={h2Class}>Watch Alex run a first round.</h2>
            <p className="mt-5 text-lg text-[#5B6478] leading-relaxed">A real interview, a real code editor, and a scorecard that fills in as the candidate works.</p>
          </Reveal>
          <Reveal delay={100}>
            <div className="mt-10 flex flex-wrap justify-center gap-2.5">
              {LIVE_STEPS.map((s, i) => {
                const state = stepTick > i ? 'done' : stepTick === i ? 'active' : 'pending';
                return (
                  <span key={s} className={`inline-flex items-center gap-2.5 rounded-full bg-white px-4 py-2.5 text-sm font-medium transition-all duration-500 ${state === 'pending' ? 'text-[#9AA3B5]' : 'text-[#1E2138]'}`} style={cardShadow}>
                    <StepIcon state={state} /> {s}
                  </span>
                );
              })}
            </div>
          </Reveal>
          <Reveal delay={160}>
            <div className="mt-10 bg-white rounded-[28px] overflow-hidden ring-1 ring-[#E3E7EE]" style={{ boxShadow: '0 40px 80px -28px rgba(27, 85, 144, 0.22)' }}>
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#EEF1F5]">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold text-[#2F6B0E]" style={{ background: 'rgba(126, 200, 69, 0.18)' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#3F8F12] q5-pulse" /> Live
                  </span>
                  <span className="text-sm font-semibold truncate">Senior Backend Engineer, round 1</span>
                </div>
                <span className="text-[13px] text-[#5B6478] hidden sm:block" style={{ fontFamily: MONO }}>23:41 elapsed</span>
              </div>
              <div className="grid lg:grid-cols-[320px_1fr_300px]">
                <div className="p-5 lg:border-r border-[#EEF1F5] space-y-4">
                  <p className="text-[13px] font-semibold text-[#5B6478]">Conversation</p>
                  {CONVERSATION.map((m, i) => (
                    <div key={i} className="rounded-2xl px-4 py-3" style={{ background: m.ai ? 'rgba(0, 169, 157, 0.08)' : '#F5F7FA' }}>
                      <p className="text-xs font-semibold mb-1" style={{ color: m.ai ? '#00786F' : SLATE }}>{m.who}</p>
                      <p className="text-sm leading-relaxed">{m.text}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col bg-[#0F1B2D] min-w-0">
                  <div className="flex items-center justify-between px-4 py-3 bg-[#16263D] text-[13px]" style={{ fontFamily: MONO }}>
                    <span className="text-[#E6EEF7]">rate_limiter.py</span>
                    <span className="text-[#7F93AD] text-xs">Python 3.12</span>
                  </div>
                  <div className="flex-1 overflow-x-auto px-4 py-4">
                    <pre className="text-[13px] leading-[1.75] m-0" style={{ fontFamily: MONO }}>
                      {CODE_LINES.map((line, i) => (
                        <div key={i} className="flex">
                          <span className="select-none w-7 pr-4 text-right text-[#4E6480] shrink-0">{i + 1}</span>
                          <span className="whitespace-pre">
                            {line.length === 0 ? ' ' : line.map(([kind, text], j) => <span key={j} style={{ color: CODE_COLORS[kind] }}>{text}</span>)}
                          </span>
                        </div>
                      ))}
                    </pre>
                  </div>
                  <div className="flex items-center gap-2.5 px-4 py-3 bg-[#16263D] text-[13px]" style={{ fontFamily: MONO }}>
                    <span className="w-2 h-2 rounded-full bg-[#7EC845] shrink-0" />
                    <span className="text-[#E6EEF7]">6 of 7 tests passing</span>
                    <span className="text-[#7F93AD] text-xs truncate">failing: test_window_boundary_race</span>
                  </div>
                </div>
                <div className="p-5 lg:border-l border-[#EEF1F5] space-y-4">
                  <p className="text-[13px] font-semibold text-[#5B6478]">Live scorecard</p>
                  <p className="flex items-baseline gap-2">
                    <span className="text-[40px] font-semibold leading-none tracking-[-0.03em]">312</span>
                    <span className="text-[15px] font-medium text-[#5B6478]">of 400</span>
                  </p>
                  {SCORE_DIMENSIONS.map(([name, v]) => (
                    <div key={name}>
                      <div className="flex justify-between text-[13px] mb-1.5">
                        <span className="font-medium">{name}</span>
                        <span className="text-[#5B6478] text-xs" style={{ fontFamily: MONO }}>{v} / 80</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-[#EEF1F5]">
                        <div className="h-1.5 rounded-full" style={{ width: `${(v / 80) * 100}%`, background: SCORE_GRADIENT }} />
                      </div>
                    </div>
                  ))}
                  <div className="rounded-2xl bg-[#F5F7FA] px-4 py-3 space-y-2">
                    <p className="text-[13px] font-semibold">Integrity</p>
                    {[['#7EC845', 'Identity matched to screening test'], ['#C77700', 'Switched tabs once, 4 seconds'], ['#C77700', 'Pasted 38 characters at 14:02']].map(([c, t]) => (
                      <p key={t} className="flex items-center gap-2 text-[13px]"><span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: c }} />{t}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ========== HOW IT WORKS: 01 / 02 / 03 ========== */}
      <section id="how" className="py-16 md:py-28">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl">
            <h2 className={h2Class}>From intake call to scored shortlist, without pulling in an engineer.</h2>
            <p className="mt-5 text-lg text-[#5B6478] leading-relaxed">Your recruiter starts it. Your hiring manager makes the call. Quali5d does the work in between.</p>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {HOW.map((s, i) => (
              <Reveal key={s.title} delay={i * 110}>
                <div className="h-full rounded-[28px] bg-[#F5F7FA] p-7 md:p-8 transition-transform duration-500 hover:-translate-y-1">
                  <span className="text-sm font-semibold text-[#9AA3B5]" style={{ fontFamily: MONO }}>{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="mt-10 text-xl font-semibold tracking-[-0.02em]">{s.title}</h3>
                  <p className="mt-3 text-[15px] text-[#5B6478] leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ========== INTEGRITY & TRUST ========== */}
      <section id="integrity" className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <h2 className={h2Class}>An interviewer you can count on.</h2>
            <p className="mt-5 text-lg text-[#5B6478] leading-relaxed">Three reasons talent leaders hand Alex the first round.</p>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {TRUST.map((c, i) => (
              <Reveal key={c.title} delay={i * 110}>
                <div className="h-full rounded-[28px] bg-white border border-[#EEF1F5] p-7 md:p-8 transition-transform duration-500 hover:-translate-y-1" style={cardShadow}>
                  <Blob color={c.color} size={48} shape={i} />
                  <h3 className="mt-8 text-xl font-semibold tracking-[-0.02em]">{c.title}</h3>
                  <p className="mt-3 text-[15px] text-[#5B6478] leading-relaxed">{c.text}</p>
                  <ul className="mt-6 space-y-2.5">
                    {c.points.map(p => (
                      <li key={p} className="flex items-center gap-2.5 text-[14px]"><span className="w-1.5 h-1.5 rounded-full bg-[#7EC845] shrink-0" />{p}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center mt-10">
            <a href="/security" className="text-[15px] font-semibold text-[#1E2138] underline decoration-[#E3E7EE] underline-offset-4 hover:decoration-[#1E2138] transition-colors">Read our security practices</a>
          </Reveal>
        </div>
      </section>

      {/* ========== PRICING ========== */}
      <section id="pricing" className="py-16 md:py-24 bg-[#F5F7FA]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto">
            <h2 className={h2Class}>Flat monthly pricing. Not per interview.</h2>
            <p className="mt-5 text-lg text-[#5B6478] leading-relaxed">Your fiftieth screen costs the same as your first.</p>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {PLANS.map((p, i) => (
              <Reveal key={p.name} delay={i * 110}>
                <div className={`h-full rounded-[28px] p-7 md:p-8 flex flex-col ${p.highlight ? 'bg-[#1E2138] text-white' : 'bg-white border border-[#EEF1F5]'}`} style={p.highlight ? { boxShadow: '0 30px 60px -24px rgba(30, 33, 56, 0.45)' } : cardShadow}>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h3 className="text-lg font-semibold">{p.name}</h3>
                    {p.highlight && <span className="rounded-full bg-[#7EC845] px-2.5 py-1 text-xs font-semibold text-[#1E2138]">Includes AI interviews</span>}
                  </div>
                  <p className="mt-6 flex items-baseline gap-1.5">
                    <span className="text-[44px] font-semibold leading-none tracking-[-0.03em]">{p.price}</span>
                    <span className={`text-[15px] ${p.highlight ? 'text-white/70' : 'text-[#5B6478]'}`}>per month</span>
                  </p>
                  <p className={`mt-4 text-[15px] leading-relaxed ${p.highlight ? 'text-white/75' : 'text-[#5B6478]'}`}>{p.tag}</p>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {p.features.map(f => (
                      <li key={f} className="flex items-center gap-2.5 text-[15px]"><span className="w-1.5 h-1.5 rounded-full bg-[#7EC845] shrink-0" />{f}</li>
                    ))}
                  </ul>
                  <button onClick={() => scrollToId('pilot')} className={`mt-8 w-full ${p.highlight ? 'inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-[15px] font-semibold text-[#1E2138] transition-all duration-300 hover:bg-white/90' : pillLight}`}>
                    {p.highlight ? 'Start a free pilot' : 'Get started'}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center mt-10">
            <a href="/pricing" onClick={goPricingPage} className="text-[15px] font-semibold text-[#1E2138] underline decoration-[#D5DBE5] underline-offset-4 hover:decoration-[#1E2138] transition-colors">Compare plans and add-on packs</a>
          </Reveal>
        </div>
      </section>

      {/* ========== FAQ ========== */}
      <section id="faq" className="py-16 md:py-28">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal className="text-center">
            <h2 className={h2Class}>Questions</h2>
          </Reveal>
          <div className="mt-12">
            {FAQS.map(([q, a], i) => (
              <Reveal key={q} delay={i * 60}>
                <div className="border-b border-[#EEF1F5]">
                  <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center gap-5 py-6 text-left" aria-expanded={openFaq === i}>
                    <span className="text-sm font-semibold text-[#9AA3B5] w-6 shrink-0" style={{ fontFamily: MONO }}>{String(i + 1).padStart(2, '0')}</span>
                    <span className="flex-1 text-lg font-semibold tracking-[-0.01em]">{q}</span>
                    <span className={`w-8 h-8 rounded-full bg-[#F5F7FA] flex items-center justify-center shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-45' : ''}`}>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" d="M12 5v14M5 12h14" /></svg>
                    </span>
                  </button>
                  <div className="grid transition-all duration-500" style={{ gridTemplateRows: openFaq === i ? '1fr' : '0fr' }}>
                    <div className="overflow-hidden">
                      <p className="pb-6 pl-11 pr-12 text-[15px] text-[#5B6478] leading-relaxed">
                        {a}
                        {i === 3 && <> <a href="/integrations" className="font-semibold text-[#1E2138] underline underline-offset-4">See integrations</a></>}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CLOSING CTA ========== */}
      <section className="px-5 lg:px-8 pb-6">
        <Reveal className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-[32px] px-6 py-20 md:py-24 text-center" style={{ background: 'linear-gradient(135deg, #EAF6FD 0%, #F2F9EC 100%)' }}>
            <div aria-hidden="true" className="absolute -left-10 top-10 opacity-80"><Blob color="#8ED8FB" size={90} shape={0} /></div>
            <div aria-hidden="true" className="absolute right-10 -bottom-6 opacity-80"><Blob color="#B5E08A" size={110} shape={1} /></div>
            <div aria-hidden="true" className="absolute right-24 top-8 opacity-70"><Blob color="#B9B3F5" size={54} shape={2} /></div>
            <h2 className={`relative ${h2Class}`}>Ready to give your engineers their week back?</h2>
            <p className="relative mt-5 text-lg text-[#5B6478] max-w-xl mx-auto leading-relaxed">Send one open role. We will screen up to 10 candidates free and send your hiring manager the scored reports.</p>
            <div className="relative mt-8 flex flex-wrap justify-center gap-3">
              <button onClick={() => scrollToId('pilot')} className={pillDark}>Start a free pilot</button>
              <button onClick={() => scrollToId('pilot')} className={pillLight}>Book a 20-minute walkthrough</button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ========== PILOT / DEMO REQUEST ========== */}
      <section id="pilot" className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-5 lg:px-8">
          <Reveal className="text-center mb-10">
            <h2 className={h2Class}>Start a free pilot</h2>
            <p className="mt-4 text-lg text-[#5B6478]">Leave your details and we will reach out within one business day.</p>
          </Reveal>
          <Reveal delay={100}>
            <div className="rounded-[28px] bg-white border border-[#EEF1F5] p-7 md:p-9" style={cardShadow}>
              {demoSuccess ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(126, 200, 69, 0.22)' }}>
                    <svg className="w-7 h-7 text-[#2F6B0E]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <h3 className="text-2xl font-semibold tracking-[-0.02em]">Request received</h3>
                  <p className="mt-2 text-[#5B6478]">Thank you. Our team will be in touch within one business day.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">First name *</label>
                      <input type="text" value={demoForm.firstName} onChange={e => setDemoForm(p => ({ ...p, firstName: e.target.value }))} placeholder="John" className={inputClass} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Last name *</label>
                      <input type="text" value={demoForm.lastName} onChange={e => setDemoForm(p => ({ ...p, lastName: e.target.value }))} placeholder="Smith" className={inputClass} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Country *</label>
                    <select value={demoForm.country} onChange={e => setDemoForm(p => ({ ...p, country: e.target.value }))} className={inputClass}>
                      <option value="">Select your country</option>
                      {COUNTRIES.map(([name, code]) => (
                        <option key={name} value={`${name} (${code})`}>{name} ({code})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Phone number *</label>
                    <input type="tel" value={demoForm.phone} onChange={e => setDemoForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 555 000 0000" className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Work email *</label>
                    <input type="email" value={demoForm.email} onChange={e => setDemoForm(p => ({ ...p, email: e.target.value }))} placeholder="you@yourcompany.com" className={inputClass} />
                    <p className="text-xs text-[#5B6478] mt-1.5">Please use your company email, not Gmail, Yahoo or Hotmail.</p>
                  </div>
                  {demoError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{demoError}</div>
                  )}
                  <button disabled={demoLoading} onClick={handleDemoSubmit} className={`${pillDark} w-full disabled:opacity-60`}>
                    {demoLoading ? 'Submitting...' : 'Request my pilot'}
                  </button>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="border-t border-[#EEF1F5] pt-16 pb-10">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10 mb-14">
            <div>
              <img src="/quali5d-logo.png" alt="Quali5d" width="100" height="64" className="h-16 w-auto" />
              <p className="mt-4 text-[15px] text-[#5B6478] leading-relaxed max-w-xs">Live AI technical interviews for in-house engineering hiring teams.</p>
              <a href="mailto:admin@quali5d.com" className="inline-block mt-3 text-[15px] font-medium hover:opacity-70">admin@quali5d.com</a>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4">Product</h4>
              <ul className="space-y-3 text-[15px] text-[#5B6478]">
                <li><a href="#how" onClick={(e) => { e.preventDefault(); scrollToId('how'); }} className="hover:text-[#1E2138]">How it works</a></li>
                <li><a href="/pricing" onClick={goPricingPage} className="hover:text-[#1E2138]">Pricing</a></li>
                <li><a href="/integrations" className="hover:text-[#1E2138]">Integrations</a></li>
                <li><a href="/security" className="hover:text-[#1E2138]">Security</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4">Company</h4>
              <ul className="space-y-3 text-[15px] text-[#5B6478]">
                <li><button onClick={() => scrollToId('pilot')} className="hover:text-[#1E2138]">Contact</button></li>
                <li><button onClick={openLogin} className="hover:text-[#1E2138]">Sign in</button></li>
                <li><button onClick={() => scrollToId('pilot')} className="hover:text-[#1E2138]">Start a free pilot</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4">Legal</h4>
              <ul className="space-y-3 text-[15px] text-[#5B6478]">
                <li><a href="/terms" className="hover:text-[#1E2138]">Terms</a></li>
                <li><a href="/privacy" className="hover:text-[#1E2138]">Privacy</a></li>
                <li><a href="/acceptable-use" className="hover:text-[#1E2138]">Acceptable use</a></li>
                <li><a href="/gdpr" className="hover:text-[#1E2138]">GDPR</a></li>
              </ul>
            </div>
          </div>
          <p className="text-[13px] text-[#5B6478] pt-6 border-t border-[#EEF1F5]">© 2026 Uni5d Technology Solutions LLC</p>
        </div>
      </footer>

      {/* ========== LOGIN MODAL ========== */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 bg-[#1E2138]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowLoginModal(false)} data-lenis-prevent>
          <div className="bg-white rounded-[28px] shadow-2xl p-8 w-full max-w-md relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowLoginModal(false)} className="absolute top-5 right-5 w-8 h-8 rounded-full bg-[#F5F7FA] flex items-center justify-center text-[#5B6478] hover:text-[#1E2138] transition-colors" aria-label="Close">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h2 className="text-2xl font-semibold tracking-[-0.02em]">Welcome back</h2>
            <p className="text-[#5B6478] mt-1 mb-6 text-sm">Sign in to your Quali5d workspace</p>
            <form onSubmit={handleLoginSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Email or username</label>
                <input type="text" name="usernameOrEmail" value={loginData.usernameOrEmail} onChange={handleLoginInputChange} placeholder="name@company.com" autoComplete="username" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Password</label>
                <input type="password" name="password" value={loginData.password} onChange={handleLoginInputChange} placeholder="Enter your password" autoComplete="current-password" className={inputClass} />
              </div>
              {loginError && (<div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{loginError}</div>)}
              <button type="submit" disabled={isLoggingIn} className={`${pillDark} w-full disabled:opacity-60`}>
                {isLoggingIn ? 'Signing in...' : 'Sign in to Quali5d'}
              </button>
            </form>
            <div className="mt-5 flex items-center justify-between">
              <button type="button" onClick={() => { setShowLoginModal(false); setShowForgotPassword(true); }} className="text-sm font-medium text-[#5B6478] hover:text-[#1E2138]">Forgot password?</button>
              <button onClick={() => { setShowLoginModal(false); navigate('/pricing'); }} className="text-sm font-semibold text-[#1E2138] hover:opacity-70">Create an account</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Forgot Password Modal ──────────────────────────────────────────── */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 bg-[#1E2138]/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowForgotPassword(false)} data-lenis-prevent>
          <div className="bg-white rounded-[28px] shadow-2xl w-full max-w-md p-8 relative"
            onClick={e => e.stopPropagation()}>

            {/* Close button */}
            <button
              onClick={() => setShowForgotPassword(false)}
              className="absolute top-5 right-5 w-8 h-8 rounded-full bg-[#F5F7FA] flex items-center justify-center text-[#5B6478] hover:text-[#1E2138] transition-colors"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            {forgotSent ? (
              /* ── Sent state ── */
              <div className="text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(126, 200, 69, 0.22)' }}>
                  <span className="text-2xl">📧</span>
                </div>
                <h2 className="text-lg font-semibold">Check your inbox</h2>
                <p className="text-sm text-[#5B6478] mt-2 mb-5">{forgotMessage}</p>
                <p className="text-xs text-[#5B6478] mb-6">
                  Didn't receive it? Check your spam folder or try again.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setForgotSent(false); setForgotMessage(''); }}
                    className={`${pillLight} flex-1`}
                  >
                    Try again
                  </button>
                  <button
                    onClick={() => { setShowForgotPassword(false); setShowLoginModal(true); }}
                    className={`${pillDark} flex-1`}
                  >
                    Back to sign in
                  </button>
                </div>
              </div>
            ) : (
              /* ── Email form ── */
              <>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-[#F5F7FA] flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">🔐</span>
                  </div>
                  <h2 className="text-lg font-semibold">Forgot password?</h2>
                  <p className="text-sm text-[#5B6478] mt-1">
                    Enter your registered email and we'll send you a reset link.
                  </p>
                </div>

                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      Email address
                    </label>
                    <input
                      type="email"
                      required
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      placeholder="Enter your registered email"
                      className={inputClass}
                    />
                  </div>

                  {forgotMessage && (
                    <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                      {forgotMessage}
                    </div>
                  )}

                  <button type="submit" disabled={forgotLoading} className={`${pillDark} w-full disabled:opacity-60`}>
                    {forgotLoading ? 'Sending reset link...' : 'Send reset link'}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(false); setShowLoginModal(true); }}
                    className="w-full py-2.5 text-sm text-[#5B6478] hover:text-[#1E2138] font-medium transition-colors"
                  >
                    Back to sign in
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Welcome Toast (shown after Stripe payment redirect) ── */}
      {showWelcomeToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60]">
          <div className="bg-white ring-1 ring-[#EEF1F5] shadow-2xl rounded-2xl px-6 py-4 flex items-center gap-4 min-w-[340px] max-w-lg">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(126, 200, 69, 0.22)' }}>
              <svg className="w-5 h-5 text-[#2F6B0E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">🎉 Welcome to Quali5d!</p>
              <p className="text-[#5B6478] text-xs mt-0.5">
                Your account is now active. Please log in to get started.
              </p>
            </div>
            <button onClick={() => setShowWelcomeToast(false)} className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2" aria-label="Dismiss">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LandingPage;